/**
 * Shared Clerk `appearance` config — applied via `<ClerkProvider appearance>`
 * in [LocalizedClerkProvider](./components/LocalizedClerkProvider.tsx) so every
 * Clerk widget (`<SignIn/>`, `<SignUp/>`, `<UserButton/>`, `<UserProfile/>`,
 * `<OrganizationProfile/>`, `<OrganizationSwitcher/>`, `<PricingTable/>`)
 * picks up the eehae visual language without per-mount overrides.
 *
 * Strategy:
 *   - `variables` carry the global token mapping. Values reference the CSS
 *     custom properties defined in `styles/tokens.css` (`--ink`,
 *     `--paper-card`, `--violet-*`, `--r-*`, font tokens) so theme switches
 *     via the `[data-theme="dark"]` attribute on `<html>` flow through
 *     automatically.
 *   - `elements` overrides target the most-visible Clerk DOM hooks: the
 *     widget card, header text, primary CTA, form inputs, social buttons,
 *     dividers, footer link.
 *   - `layout` puts the logo inside the card and pushes social buttons to
 *     the top.
 *
 * Per ADR 0073 the widget sits directly on the page: the card has no
 * border, fill or shadow, the type is the body family, and the CTA is the
 * ink button with 6px corners.
 *
 * No icon URL is set on `layout.logoImageUrl`. Clerk falls back to the
 * dashboard-configured logo, which is the right behavior until the design
 * lands a finalized logo asset.
 *
 * Color refs use `var(--name)` strings rather than literal hex so the same
 * config drives both the light theme and the dark variant defined under
 * `[data-theme="dark"]` in `styles/themes.css`.
 */
import type { Appearance } from '@clerk/types'

export const clerkAppearance: Appearance = {
  variables: {
    // Brand + semantic colors
    colorPrimary: 'var(--ink)',
    colorPrimaryForeground: '#ffffff',
    colorBackground: 'var(--paper-card)',
    colorForeground: 'var(--ink)',
    colorMutedForeground: 'var(--ink-muted)',
    colorMuted: 'var(--paper-soft)',
    colorBorder: 'var(--paper-line)',
    colorInput: 'var(--paper-card)',
    colorInputForeground: 'var(--ink)',
    colorRing: 'var(--violet-100)',
    colorDanger: 'var(--bad)',
    colorSuccess: 'var(--ok)',
    colorWarning: 'var(--warn)',

    // Type — one family, body weight
    fontFamily:
      "Pretendard, Inter, system-ui, -apple-system, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif",
    fontFamilyButtons:
      "Pretendard, Inter, system-ui, -apple-system, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif",
    fontSize: '14px',

    // Radii — Clerk derives sm/lg/xl from this base
    borderRadius: 'var(--r-sm)',
  },

  elements: {
    // The widget is the page, not a card on it.
    card: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      boxShadow: 'none',
    },

    // Header text — body family, ink title over muted subtitle
    headerTitle: {
      fontFamily: 'var(--font-body)',
      fontSize: '22px',
      fontWeight: 700,
      letterSpacing: '-0.005em',
      color: 'var(--ink)',
    },
    headerSubtitle: {
      fontSize: '14px',
      color: 'var(--ink-muted)',
    },

    // Primary CTA — the ink button from styles/base.css: 44px tall,
    // 6px corners, body font at 500, sentence case.
    formButtonPrimary: {
      backgroundColor: 'var(--ink)',
      color: '#ffffff',
      borderRadius: 'var(--r-sm)',
      minHeight: '44px',
      fontFamily: 'var(--font-body)',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: 0,
      '&:hover': {
        backgroundColor: 'var(--ink-2)',
      },
      '&:focus': {
        boxShadow: '0 0 0 4px rgba(124, 58, 237, 0.12)',
      },
    },

    // Form fields — 44px hairline inputs with the app's violet focus
    // ring.
    formFieldInput: {
      backgroundColor: 'var(--paper-card)',
      borderColor: 'var(--paper-line)',
      borderRadius: 'var(--r-sm)',
      minHeight: '44px',
      color: 'var(--ink)',
      '&:focus': {
        borderColor: 'var(--violet-400)',
        boxShadow: '0 0 0 4px rgba(124, 58, 237, 0.12)',
      },
    },
    formFieldLabel: {
      color: 'var(--ink)',
      fontWeight: 500,
    },

    // Social SSO buttons — the hairline secondary button.
    socialButtonsBlockButton: {
      backgroundColor: 'transparent',
      borderColor: 'var(--paper-line-2)',
      borderRadius: 'var(--r-sm)',
      minHeight: '44px',
      color: 'var(--ink-2)',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: 'var(--paper-soft)',
      },
    },

    // Divider — a hairline with a faint "or"
    dividerLine: {
      backgroundColor: 'var(--paper-line)',
    },
    dividerText: {
      fontSize: '12px',
      color: 'var(--ink-faint)',
    },

    // Footer "Sign up" / "Sign in" cross-link — the accent violet.
    footerActionLink: {
      color: 'var(--violet-700)',
      fontWeight: 500,
      '&:hover': {
        color: 'var(--violet-600)',
      },
    },

    // UserProfile / OrganizationProfile section titles
    profileSectionTitleText: {
      fontFamily: 'var(--font-body)',
      fontWeight: 600,
      color: 'var(--ink)',
    },
  },

  layout: {
    // Logo inside the card so the split-screen layout's left panel can
    // own the dominant brand mark; the in-card logo stays small and
    // referential.
    logoPlacement: 'inside',
    socialButtonsPlacement: 'top',
    socialButtonsVariant: 'blockButton',
    animations: true,
    privacyPageUrl: '/privacy',
    termsPageUrl: '/terms',
  },
}
